import React, {FC, useState, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { billingService } from '../../../api';
import {
  BackArrow,
  Typography,
  InfoBlock,
  Button,
  Table,
} from '../../../components';
import { useUi } from '../../../context/ui.context';
import { usePageTitle } from '../../../context/navigationContext';
import {
  Container,
  Header,
  RowHeaderComponent,
  StatusDot,
  StatusText,
  StatusContainer,
  ServiceBody,
  TopInfoWrapper,
  ActionButtonsWrapper,
  ConfigOptionsContainer,
  RightSideComponent,
  StyledIconMore,
  MenuContainer,
  TabsWrapper,
} from './styles';
import { timeConverterMMDDYYHHMM } from '../../../utils/dates';
import ServiceSkeleton from './skeleton'
import { handleStatusName } from '../../../utils/handle-status-name';
import { icons } from '../../../icons';
import DropOptions from '../../../components/drop-options';
import Tabs from '../../../components/tabs';
import ServiceCyclesTab from './service-cycles';
import Dialog from '../../../components/dialog';
import UniversalModal from '../../../components/modal-alerts/universal-modal';
import ServiceDeleteModal from './modals/service-delete-modal';
import ServiceEnableBillingModal from './modals/service-enable-billing-modal';
import ServiceChangeProjectModal from './modals/service-change-project-modal';

const ServicePage:FC = () => {
  const [t] = useTranslation()
  const {viewAlert} = useUi()
  const navigate = useNavigate()
  const [service, setService] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const {serviceId} = useParams()
  usePageTitle(service?.product?.name ? `${t("SERVICE")}: ${service.product.name}` : t("SERVICE"));
  const [tabParam, setTabParam] = useQueryParam('tab', StringParam);
  const [activeTab, setActiveTab] = useState<string>(tabParam || 'overview')
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const menuRef = useRef<any>(null)
  const buttonRef = useRef<any>(null)

  // Modal states
  const [suspendModalOpen, setSuspendModalOpen] = useState(false)
  const [resumeModalOpen, setResumeModalOpen] = useState(false)
  const [activateModalOpen, setActivateModalOpen] = useState(false)
  const [terminateModalOpen, setTerminateModalOpen] = useState(false)
  const [issueInvoiceModalOpen, setIssueInvoiceModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [enableBillingModalOpen, setEnableBillingModalOpen] = useState(false)
  const [changeProjectModalOpen, setChangeProjectModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const getService = () => {
    if(serviceId){
      billingService.getService(serviceId)
      .then((res) => {
        setService(res.data)
        setPageLoading(false)
      })
      .catch((err) => {
        if(err?.response?.data?.detail){
          viewAlert({
            severity: 'error',
            message: err.response.data.detail
          })
        }
        navigate(-1)
      })
    }
  }

  useEffect(() => {
    getService()

    const interval = setInterval(getService, 5000)

    return () => clearInterval(interval)
  },[serviceId])

  useEffect(() => {
    setActiveTab(tabParam || 'overview')
  }, [tabParam])

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  // Action handlers
  const handleSuspend = async () => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.suspendService(service.id)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('SERVICE_SUSPENDED_SUCCESSFULLY')
      })
      setSuspendModalOpen(false)
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_SUSPEND_SERVICE')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleResume = async () => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.resumeService(service.id)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('SERVICE_RESUMED_SUCCESSFULLY')
      })
      setResumeModalOpen(false)
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_RESUME_SERVICE')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleActivate = async () => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.activateService(service.id)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('SERVICE_ACTIVATED_SUCCESSFULLY')
      })
      setActivateModalOpen(false)
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_ACTIVATE_SERVICE')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleTerminate = async () => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.terminateService(service.id)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('SERVICE_TERMINATED_SUCCESSFULLY')
      })
      setTerminateModalOpen(false)
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_TERMINATE_SERVICE')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleIssueInvoice = async () => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.issueInvoice(service.id)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('INVOICE_ISSUED_SUCCESSFULLY')
      })
      setIssueInvoiceModalOpen(false)
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_ISSUE_INVOICE')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (deleteOnlyFromDb: boolean, deleteAssociatedResources: boolean) => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.deleteService(service.id, {
        delete_only_from_db: deleteOnlyFromDb,
        delete_associated_resources: deleteAssociatedResources
      })
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('SERVICE_DELETED_SUCCESSFULLY')
      })
      setDeleteModalOpen(false)
      navigate('/billing/services')
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_DELETE_SERVICE')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleEnableBilling = async (newCycleStart: string) => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.enableBilling(service.id, newCycleStart)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('BILLING_ENABLED_SUCCESSFULLY')
      })
      setEnableBillingModalOpen(false)
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_ENABLE_BILLING')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleChangeProject = async (projectId: string) => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.changeResourceAssociation(service.id, projectId)
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('PROJECT_CHANGED_SUCCESSFULLY')
      })
      setChangeProjectModalOpen(false)
      navigate('/billing/services')
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || t('FAILED_TO_CHANGE_PROJECT')
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleCustomAction = async (actionKey: string, actionDefinition: any) => {
    if (!service) return
    setActionLoading(true)
    try {
      const res = await billingService.executeAction(service.id, { action: actionKey })
      viewAlert({
        severity: 'success',
        message: res.data?.detail || `${actionDefinition.display_name} executed successfully`
      })
      getService()
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || `Failed to execute ${actionDefinition.display_name}`
      })
    } finally {
      setActionLoading(false)
    }
  }

  if(pageLoading) return <ServiceSkeleton pageLoading={pageLoading}/>
  if (!service) return null;

  const tabs = [
    {
      id:'overview',
      slug:'overview',
      title:t('OVERVIEW')
    },
    {
      id:'service-cycles',
      slug:'service-cycles',
      title:t('SERVICE_CYCLES', { defaultValue: 'Service cycles' })
    }
  ]

  const dataTimeline = [
    {
      title:t("CREATED_AT"),
      id:'1',
      value:timeConverterMMDDYYHHMM(service.created_at) || '-',
    },
    {
      title:t("NEXT_INVOICE_DATE"),
      id:'2',
      value:service.next_invoice_date ? timeConverterMMDDYYHHMM(service.next_invoice_date) : 'n/a',
    },
    {
      title:t("PAID_UNTIL"),
      id:'3',
      value:service.paid_until ? timeConverterMMDDYYHHMM(service.paid_until) : 'n/a',
    },
    {
      title:t("LAST_UPDATE"),
      id:'4',
      value:timeConverterMMDDYYHHMM(service.updated_at) || '-',
    },
    ...(service.override_suspend_until ? [{
      title:t("DO_NOT_SUSPEND_UNTIL"),
      id:'5',
      value:timeConverterMMDDYYHHMM(service.override_suspend_until),
    }] : []),
    ...(service.auto_terminate_date ? [{
      title:t("AUTO_TERMINATE_DATE"),
      id:'6',
      value:timeConverterMMDDYYHHMM(service.auto_terminate_date),
    }] : []),
    ...(service.activated_at ? [{
      title:t("ACTIVATED_AT"),
      id:'7',
      value:timeConverterMMDDYYHHMM(service.activated_at),
    }] : []),
    ...(service.suspended_at ? [{
      title:t("SUSPENDED_AT"),
      id:'8',
      value:timeConverterMMDDYYHHMM(service.suspended_at),
    }] : []),
    ...(service.terminated_at ? [{
      title:t("TERMINATED_AT"),
      id:'9',
      value:timeConverterMMDDYYHHMM(service.terminated_at),
    }] : []),
  ];

  const dataProduct = [
    ...(service.order ? [{
      title:t("ORDER"),
      id:'order',
      value: <a href={`/orders/${service.order}`}>{service.order}</a>,
    }] : []),
    {
      title:t("PRODUCT"),
      id:'product',
      value: service.product?.name ? (
        <a href={`/billing/products/${service.product.id}`}>{service.product.name}</a>
      ) : '-',
    },
    {
      title:t("DESCRIPTION"),
      id:'2',
      value:service.product?.description || '-',
    },
    {
      title:t("PAYMENT"),
      id:'3',
      value:service.cycle?.display_name || '-',
    },
    {
      title:t("SETUP_FEE"),
      id:'4',
      value:service.cycle?.setup_fee ? `${service.cycle.setup_fee} ${service.cycle?.currency || ''}` : '0',
    },
    ...(service.resources?.service_resources?.name ? [{
      title:t("PROJECT_NAME"),
      id:'5',
      value:service.resources.service_resources.name,
    }] : []),
    {
      title:t("BILLING_CYCLE"),
      id:'6',
      value:service.cycle?.display_name || '-',
    },
    {
      title:t("CURRENCY"),
      id:'7',
      value:service.currency?.code || '-',
    },
    {
      title:t("IS_FREE"),
      id:'8',
      value: service.free_of_charge ? (
        service.is_free ? 'Yes (service marked as free)' : 'Yes (product is free)'
      ) : 'No',
    },
    {
      title:t("RENEW_ENABLED"),
      id:'9',
      value: service.renew_enabled !== null && service.renew_enabled !== undefined ? (service.renew_enabled ? 'Yes' : 'No') : '-',
    },
    ...(service.override_price && !service.is_free ? [{
      title:t("OVERRIDE_PRICE"),
      id:'10',
      value: `${service.override_price} ${service.currency?.code || ''}`,
    }] : []),
    ...(service.current_price !== null && service.current_price !== undefined ? [{
      title:t("PRICE_WITHOUT_TAXES"),
      id:'11',
      value: `${service.current_price} ${service.cycle?.currency || service.currency?.code || ''}${service.override_price ? ' (overridden)' : ''}`,
    }] : []),
    ...(service.client ? [{
      title:t("CLIENT"),
      id:'client',
      value: <a href={`/clients-users/clients/${service.client.id}`}>{service.client.display || service.client.name}</a>,
    }] : []),
    ...(service.last_service_cycle_state && ['payment canceled', 'refunded'].includes(service.last_service_cycle_state) ? [{
      title:t("BILLING_STATUS"),
      id:'billing_status',
      value: `Billing is disabled because last cycle state is ${service.last_service_cycle_state}`,
    }] : []),
  ];

  const showEditButton = ['pending', 'active'].includes(service.status);
  const showUpgradeButton = ['pending', 'active'].includes(service.status);

  // Build menu items based on service status
  const buildMenuItems = () => {
    const items: any[] = [];

    // Edit - always available
    items.push({
      title: t("EDIT"),
      id: 'edit',
      icon: icons.Edit || '✏️',
      divider: false,
      onClick: () => {
        navigate(`/billing/services/${serviceId}/edit`)
      },
    });

    // Status-based actions
    switch (service.status) {
      case 'active':
        items.push({
          title: t("SUSPEND_SERVICE"),
          id: 'suspend',
          icon: icons.PauseIcon,
          divider: false,
          onClick: () => setSuspendModalOpen(true),
        });
        break;
      case 'suspended':
        items.push({
          title: t("RESUME_SERVICE"),
          id: 'resume',
          icon: icons.PlayIcon,
          divider: false,
          onClick: () => setResumeModalOpen(true),
        });
        break;
      case 'pending':
      case 'terminated':
        items.push({
          title: t("ACTIVATE_SERVICE"),
          id: 'activate',
          icon: icons.PlusDefault,
          divider: false,
          onClick: () => setActivateModalOpen(true),
        });
        break;
    }

    // Enable billing - only when last_service_cycle_state is 'payment canceled' or 'refunded'
    if (service.last_service_cycle_state &&
        ['payment canceled', 'refunded'].includes(service.last_service_cycle_state)) {
      items.push({
        title: t("ENABLE_BILLING"),
        id: 'enable_billing',
        icon: icons.PlayIcon,
        divider: false,
        onClick: () => setEnableBillingModalOpen(true),
      });
    }

    // Terminate - available for all non-terminated services
    if (service.status !== 'terminated') {
      items.push({
        title: t("TERMINATE_SERVICE"),
        id: 'terminate',
        icon: icons.CloseIcon,
        divider: false,
        onClick: () => setTerminateModalOpen(true),
      });
    }

    // Issue invoice - only for active or suspended
    if (['active', 'suspended'].includes(service.status)) {
      items.push({
        title: t("ISSUE_INVOICE"),
        id: 'issue_invoice',
        icon: icons.Document,
        divider: false,
        onClick: () => setIssueInvoiceModalOpen(true),
      });
    }

    // Change project - only for OpenStack products
    if (service.product?.product_type === 'openstack') {
      items.push({
        title: t("CHANGE_PROJECT"),
        id: 'change_project',
        icon: icons.Settings,
        divider: false,
        onClick: () => setChangeProjectModalOpen(true),
      });
    }

    // Custom actions from API
    if (service.actions && typeof service.actions === 'object') {
      Object.keys(service.actions).forEach((actionKey) => {
        const actionDef = service.actions[actionKey];
        items.push({
          title: actionDef.display_name,
          id: `action_${actionKey}`,
          icon: icons.Power,
          divider: false,
          onClick: () => handleCustomAction(actionKey, actionDef),
        });
      });
    }

    // Upgrade/Downgrade
    items.push({
      title: t("UPGRADE_DOWNGRADE"),
      id: 'upgrade',
      icon: icons.Resize || '⬆️',
      divider: true,
      onClick: () => {
        if (['pending', 'active'].includes(service.status)) {
          navigate(`/billing/services/${serviceId}/upgrade`)
        }
      },
      disabled: !['pending', 'active'].includes(service.status),
    });

    // Delete - always available
    items.push({
      title: t("DELETE_SERVICE"),
      id: 'delete',
      icon: icons.DeleteIcon,
      divider: false,
      onClick: () => setDeleteModalOpen(true),
    });

    return items;
  };

  const menuItems = buildMenuItems();

  const configOptionsColumns = [
    { title: t("OPTION"), key: 'option' },
    { title: t("OPTION_VALUE"), key: 'option_value' },
    { title: t("UNIT_PRICE"), key: 'unit_price' },
    { title: t("PRICE"), key: 'price' },
  ];

  const configOptionsData = service.configurable_options?.map((option: any) => ({
    option: option.display || option.option || '-',
    option_value: option.option_value || '-',
    unit_price: option.unit_price !== undefined ? `${option.unit_price} ${service.currency?.code || ''}` : '-',
    price: option.price !== undefined ? `${option.price} ${service.currency?.code || ''}` : '-',
  })) || [];

  return (
    <Container>
      <Header>
        <BackArrow fallbackPath="/billing/services"/>
        <RowHeaderComponent>
          <Typography variant='h1'>
            {service?.product?.name || 'Service'}
          </Typography>
          <RightSideComponent>
            <div style={{ position: 'relative' }} ref={buttonRef}>
              <Button
                onClick={() => setMenuOpen(!menuOpen)}
                variant='icon'
                active={menuOpen}
                isProps={true}
                size='small'
                customStyles={{width: '26px', height: '20px'}}
              >
                <StyledIconMore src={icons.More} active={menuOpen} />
              </Button>
              <MenuContainer ref={menuRef} active={menuOpen}>
                <DropOptions
                  options={menuItems}
                  itemToEdit={service}
                  handleClose={() => setMenuOpen(false)}
                />
              </MenuContainer>
            </div>
          </RightSideComponent>
        </RowHeaderComponent>
        <StatusContainer>
          <StatusDot status={service.status}/>
          <StatusText status={service.status}>
            {service?.status_display
              ? handleStatusName(service?.status_display, t)
              : handleStatusName(service?.status, t)
            }
          </StatusText>
        </StatusContainer>
      </Header>
      <TabsWrapper>
        <Tabs
          tabs={tabs}
          size='display'
          blockNavigate
          location={activeTab}
          onChange={(slug: string) => {
            setActiveTab(slug)
            setTabParam(slug)
          }}
        />
      </TabsWrapper>
      {activeTab === 'service-cycles' ? (
        serviceId ? <ServiceCyclesTab serviceId={serviceId} /> : null
      ) : (
        <ServiceBody>
          <TopInfoWrapper>
            <InfoBlock
              customStyles={{flex:'auto 1', marginBottom:'0px'}}
              title={t("PRODUCT")}
              data={dataProduct}
            />
            <InfoBlock
              customStyles={{flex:'auto 1', marginBottom:'0px'}}
              title={t("TIMELINE")}
              data={dataTimeline}
            />
          </TopInfoWrapper>

          {(showEditButton || showUpgradeButton) && (
            <ActionButtonsWrapper>
              {showEditButton && (
                <Button
                  variant="secondary"
                  title={t("EDIT_SERVICE")}
                  size="display"
                  onClick={() => navigate(`/billing/services/${serviceId}/edit`)}
                >
                  {t("EDIT_SERVICE")}
                </Button>
              )}
              {showUpgradeButton && (
                <Button
                  variant="primary"
                  title={t("UPGRADE_DOWNGRADE_BILLING")}
                  size="display"
                  onClick={() => navigate(`/billing/services/${serviceId}/upgrade`)}
                >
                  {t("UPGRADE_DOWNGRADE_BILLING")}
                </Button>
              )}
            </ActionButtonsWrapper>
          )}

          <ConfigOptionsContainer>
            <Typography variant="h2">
              {t("CONFIGURATION_OPTIONS")}
            </Typography>
            {configOptionsData.length > 0 ? (
              <Table
                columns={configOptionsColumns}
                rows={configOptionsData}
                hideCheckbox
              />
            ) : (
              <Typography variant="body">
                {t("NO_CONFIGURABLE_OPTIONS")}
              </Typography>
            )}
          </ConfigOptionsContainer>

          <TopInfoWrapper>
            <ConfigOptionsContainer>
              <Typography variant="h2">
                {t("RELATED_INVOICES")}
              </Typography>
              <a href={`/billing/invoices?filtering=items__service:${service.id}`}>
                {t("VIEW_RELATED_INVOICES")}
              </a>
            </ConfigOptionsContainer>

            {service?.hosting_account && (
              <ConfigOptionsContainer>
                <Typography variant="h2">
                  {t("HOSTING_ACCOUNT")}
                </Typography>
                <InfoBlock
                  customStyles={{ marginTop: '10px' }}
                  data={[
                    {
                      title: t("ACCOUNT"),
                      id: 'account',
                      value: service.hosting_account.account_id || '-',
                    },
                    {
                      title: t("USERNAME"),
                      id: 'username',
                      value: service.hosting_account.username || '-',
                    },
                    {
                      title: t("PACKAGE"),
                      id: 'package',
                      value: service.hosting_account.package_name || '-',
                    },
                  ]}
                />
              </ConfigOptionsContainer>
            )}
          </TopInfoWrapper>

          {service?.notes && (
            <ConfigOptionsContainer>
              <Typography variant="h2">
                {t("NOTES")}
              </Typography>
              <Typography variant="body" customStyles={{ whiteSpace: 'pre-wrap' }}>
                {service.notes}
              </Typography>
            </ConfigOptionsContainer>
          )}

          {service?.cancellation_request && (
            <ConfigOptionsContainer>
              <Typography variant="body" customStyles={{ fontWeight: 'bold', color: '#d32f2f' }}>
                {t("CANCELLATION_REQUESTED")}
              </Typography>
            </ConfigOptionsContainer>
          )}

          {service?.suspend_reason && (
            <ConfigOptionsContainer>
              <Typography variant="body" customStyles={{ fontWeight: 'bold', color: '#d32f2f' }}>
                {t("SUSPEND_REASON")}: {service.suspend_reason}
              </Typography>
            </ConfigOptionsContainer>
          )}
        </ServiceBody>
      )}

      {/* Suspend Modal */}
      <Dialog noIframe open={suspendModalOpen} handleClose={() => setSuspendModalOpen(false)}>
        <UniversalModal
          alertTitle={t("SUSPEND_SERVICE")}
          alertSubTitle={t("SUSPEND_SERVICE_CONFIRM", { name: service.display_name || service.product?.name })}
          closeModal={handleSuspend}
          closeModalCancel={() => setSuspendModalOpen(false)}
          loading={actionLoading}
          confirmButtonText={t("CANCEL")}
          cancelButtonText={t("SUSPEND_SERVICE")}
        />
      </Dialog>

      {/* Resume Modal */}
      <Dialog noIframe open={resumeModalOpen} handleClose={() => setResumeModalOpen(false)}>
        <UniversalModal
          alertTitle={t("RESUME_SERVICE")}
          alertSubTitle={t("RESUME_SERVICE_CONFIRM", { name: service.display_name || service.product?.name })}
          closeModal={handleResume}
          closeModalCancel={() => setResumeModalOpen(false)}
          loading={actionLoading}
          confirmButtonText={t("CANCEL")}
          cancelButtonText={t("RESUME_SERVICE")}
        />
      </Dialog>

      {/* Activate Modal */}
      <Dialog noIframe open={activateModalOpen} handleClose={() => setActivateModalOpen(false)}>
        <UniversalModal
          alertTitle={t("ACTIVATE_SERVICE")}
          alertSubTitle={t("ACTIVATE_SERVICE_CONFIRM", { name: service.display_name || service.product?.name })}
          closeModal={handleActivate}
          closeModalCancel={() => setActivateModalOpen(false)}
          loading={actionLoading}
          confirmButtonText={t("CANCEL")}
          cancelButtonText={t("ACTIVATE")}
        />
      </Dialog>

      {/* Terminate Modal */}
      <Dialog noIframe open={terminateModalOpen} handleClose={() => setTerminateModalOpen(false)}>
        <UniversalModal
          alertTitle={t("TERMINATE_SERVICE")}
          alertSubTitle={t("TERMINATE_SERVICE_CONFIRM", { name: service.display_name || service.product?.name })}
          closeModal={handleTerminate}
          closeModalCancel={() => setTerminateModalOpen(false)}
          loading={actionLoading}
          confirmButtonText={t("CANCEL")}
          cancelButtonText={t("TERMINATE_SERVICE")}
        />
      </Dialog>

      {/* Issue Invoice Modal */}
      <Dialog noIframe open={issueInvoiceModalOpen} handleClose={() => setIssueInvoiceModalOpen(false)}>
        <UniversalModal
          alertTitle={t("ISSUE_INVOICE")}
          alertSubTitle={t("ISSUE_INVOICE_CONFIRM", { name: service.display_name || service.product?.name })}
          closeModal={handleIssueInvoice}
          closeModalCancel={() => setIssueInvoiceModalOpen(false)}
          loading={actionLoading}
          confirmButtonText={t("CANCEL")}
          cancelButtonText={t("ISSUE_INVOICE")}
        />
      </Dialog>

      {/* Delete Modal */}
      <Dialog noIframe open={deleteModalOpen} handleClose={() => setDeleteModalOpen(false)}>
        <ServiceDeleteModal
          service={service}
          onDelete={handleDelete}
          onCancel={() => setDeleteModalOpen(false)}
          loading={actionLoading}
        />
      </Dialog>

      {/* Enable Billing Modal */}
      <Dialog noIframe open={enableBillingModalOpen} handleClose={() => setEnableBillingModalOpen(false)}>
        <ServiceEnableBillingModal
          service={service}
          onEnable={handleEnableBilling}
          onCancel={() => setEnableBillingModalOpen(false)}
          loading={actionLoading}
        />
      </Dialog>

      {/* Change Project Modal */}
      <Dialog noIframe open={changeProjectModalOpen} handleClose={() => setChangeProjectModalOpen(false)}>
        <ServiceChangeProjectModal
          service={service}
          onChangeProject={handleChangeProject}
          onCancel={() => setChangeProjectModalOpen(false)}
          loading={actionLoading}
        />
      </Dialog>
    </Container>
  )
}

export default ServicePage
